import { ErrorHandler, ERROR_CATCH_TYPE, ErrorExcluder } from "@web-monorepo/app-shell";
import { UserActionsHistory } from "@web-monorepo/telemetry/userActionsHistory";
import { isApiDownStatus } from "@web-monorepo/infra/responseHandlers";
import { type Response } from "@web-monorepo/fetchers";
import { ErrorBannerValues, is307RequestBlocked, isModuleLoadError } from "@web-monorepo/molecules";
import {
  captureException,
  captureReactException,
  unhandledAppBrokenHint,
  unhandledHint,
} from "@web-monorepo/telemetry";
import { isApiError } from "@web-monorepo/fetchers";

export { isApiError };

export const ignorableToolkitErrors: Array<ErrorExcluder> = [
  // This is a valid error that we DON'T want to ignore, but it's
  // flooding our logs on a Friday after fixing toolkit error logging.
  // See: https://linear.app/classdojo/issue/DSOL-3569/investigate-toolbox-pubnub-errors
  { checker: (error) => error.message.includes("PNPublishOperation"), id: "toolkit-pn-publish" },
  {
    checker: (error) => error.message.includes("Iframe adapter timedout on hand shake."),
    id: "toolkit-iframe-adapter-timeout",
  },
  {
    checker: (error) => error.message.includes("Unterminated regular expression literal '/;'"),
    id: "toolkit-unterminated-regex-literal",
  },
  { checker: (error) => error.message.includes("play()"), id: "toolkit-audio-play" },
];

const isIgnorableBadRequest = (response?: Response) => {
  if (!response) return false;
  return (
    response.status === 400 &&
    ["Invalid id", "Invalid targetId", "Invalid classId", "ChannelId was invalid"].includes(
      response.body?.error?.detail,
    )
  );
};

// Mostly copied from teach, but adapted to just show the error banner instead of reloading the page
// in case of 401/403 requests, etc. The error banner has a link to reload the page, so users will
// still be able to refresh the page.
export const createWebErrorHandler = (userActionsHistory: UserActionsHistory) => {
  return new ErrorHandler({
    userActionsHistory,
    // eslint-disable-next-line complexity
    onError: (error, context, errorHandlerUtils) => {
      // These errors happen when request where blocked by a filtering service. Lets just show an error banner and ignore it.
      if (is307RequestBlocked(error)) {
        errorHandlerUtils.showErrorBanner(ErrorBannerValues.requestBlocked);
        return;
      }
      if (isModuleLoadError(error)) {
        errorHandlerUtils.showErrorBanner(
          ErrorBannerValues.moduleLoadError,
          window.location.pathname + window.location.hash,
        );
        return;
      }

      const response = isApiError(error) ? error.response : undefined;

      // Send 400 bad request errors as warning (via logRequestError) and add a metric with type `frontend.badRequest`
      // Redirecting and ignoring the error here because the user probably entered a bad url, not an actionable error.
      if (isIgnorableBadRequest(response)) {
        errorHandlerUtils.logRequestErrorWithContext("badRequest", error);
        errorHandlerUtils.showErrorBanner(ErrorBannerValues.badRequest);
        return;
      }

      // Logged out errors are sent as warning (via logRequestError) and add a metric.
      // Redirecting and ignoring, not an actionable error.
      if (response && [401, 403].includes(response.status)) {
        // Log authentication/authorization failure as a warning in logs, and add a metric so alarms can be
        // triggered.
        errorHandlerUtils.logRequestErrorWithContext(response.status === 401 ? "auth.401" : "auth.403", error);
        errorHandlerUtils.showErrorBanner(ErrorBannerValues.unknown);
        return;
      }

      // Not found errors are sent as warnings (via logRequestError) and add a metric.
      // The app already knows how to handle a page not found error
      if (response && response.status === 404) {
        // Log not found errors as a warning in logs, and add a metric so alarms can be
        // triggered.
        errorHandlerUtils.logRequestErrorWithContext("request.404", error);
        errorHandlerUtils.showErrorBanner(ErrorBannerValues.unknown);
        return;
      }

      if (response && response.status === 429) {
        errorHandlerUtils.logRequestErrorWithContext("rateLimit", error);
        errorHandlerUtils.showErrorBanner(ErrorBannerValues.rateLimit);
        return;
      }

      // API down error
      if (response && isApiDownStatus(response.status)) {
        errorHandlerUtils.logRequestErrorWithContext("down", error);
        const statusCode = response.statusCode;
        const errorMessage = response.body?.error?.message || "";
        const isLoadSheddingError = errorMessage.toLowerCase().includes("load shedding");

        if (statusCode === 503 && isLoadSheddingError) {
          errorHandlerUtils.showErrorBanner(ErrorBannerValues.apiDownLoadShedding);
        } else {
          errorHandlerUtils.showErrorBanner(ErrorBannerValues.apiDown);
        }
        return;
      }

      // Which catch types end at the unknown banner decides the capture hint:
      // UNHANDLED_REJECTION shows no banner at all and MODULE_LOAD_ERROR shows its own
      // moduleLoadError banner, so only the remaining types are terminal `unknown`
      // surfaces. Same policy as home, expressed as a predicate rather than home's early
      // returns because the banner choice below is a switch, not a straight-line fallthrough.
      //
      // NOTE: the 401/403 and 404 branches above also show the unknown banner but return
      // before reaching this capture, so they keep emitting the banner's own
      // captureAppBrokenMessage — there is no exception event for them to merge into.
      const endsAtUnknownBanner =
        context.catchType !== ERROR_CATCH_TYPE.UNHANDLED_REJECTION &&
        context.catchType !== ERROR_CATCH_TYPE.MODULE_LOAD_ERROR;

      // For those, tag the exception capture itself with the app_broken tags — one rich
      // Sentry event with a stack trace, grouped by cause — and tell the banner not to
      // emit its separate message-only event for the same incident.
      const hint = endsAtUnknownBanner
        ? unhandledAppBrokenHint("error_banner", { detail: ErrorBannerValues.unknown, extra: context })
        : unhandledHint({ extra: context });

      if (context.catchType === ERROR_CATCH_TYPE.REACT_ERROR_BOUNDARY && context.componentStack) {
        captureReactException(error, { componentStack: context.componentStack }, hint, {
          appIsDead: true,
        });
      } else {
        captureException(error, hint);
      }

      switch (context.catchType) {
        case ERROR_CATCH_TYPE.REACT_ERROR_BOUNDARY: {
          errorHandlerUtils.logExceptionWithContext(error);
          errorHandlerUtils.showErrorBanner(ErrorBannerValues.unknown, undefined, { alreadyReported: true });
          break;
        }
        case ERROR_CATCH_TYPE.UNHANDLED_REJECTION: {
          errorHandlerUtils.logExceptionWithContext(error);
          break;
        }
        case ERROR_CATCH_TYPE.MODULE_LOAD_ERROR: {
          errorHandlerUtils.showErrorBanner(
            ErrorBannerValues.moduleLoadError,
            window.location.pathname + window.location.hash,
          );
          break;
        }
        // Handles unhandled errors:
        default: {
          errorHandlerUtils.logExceptionWithContext(error);
          errorHandlerUtils.showErrorBanner(ErrorBannerValues.unknown, undefined, { alreadyReported: true });
        }
      }
    },
  });
};
